import { ref, onMounted, onUnmounted } from 'vue';

/**
 * Breakpoint (px) for determining mobile devices
 */
const MOBILE_BREAKPOINT = 768;

/**
 * Composable for detecting mobile devices based on screen width
 *
 * @param {number} breakpoint - Breakpoint for mobile detection (default: 768px)
 * @returns {Object} { isMobile: Ref<boolean> } - Reactive value indicating whether it's mobile
 *
 * @example
 * const { isMobile } = useIsMobile();
 * // isMobile.value is true when screen width is less than 768px
 */
export function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const isMobile = ref(false);

  const checkMobile = () => {
    isMobile.value = window.innerWidth < breakpoint;
  };

  onMounted(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', checkMobile);
  });

  return { isMobile };
}

/**
 * Function to immediately check if the current screen width is mobile size
 *
 * @param {number} breakpoint - Breakpoint for mobile detection (default: 768px)
 * @returns {boolean} True if screen width is mobile size
 *
 * @example
 * if (isMobileSize()) {
 *   // Mobile-specific processing
 * }
 */
export function isMobileSize(breakpoint = MOBILE_BREAKPOINT) {
  return typeof window !== 'undefined' && window.innerWidth < breakpoint;
}
